import React, { FC, useEffect, useState, useMemo } from "react";
import { get, cloneDeep } from "lodash";
import { Row, Col, Collapse, Card, Spin } from "antd";
import ProgressCard from "@pages/member-details/progress-card";
import PinUnpin from "@pages/member-details/pin-unpin-component";
import CustomTooltip from "@pages/tool-tip";
import { getProgressColorCode } from "@pages/utils/utils";
import membersService from "@services/members.service";
import memberDetailsService from "@services/memberDetails.service";
import { useKeyNumbersStore } from "@stores/keyNumbersStore";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_FOUND } from "../../../../constants/constants";
import { centricClientCheck } from "@utils/helpers";

const { Panel } = Collapse;

const KeyNumbers: FC<any> = (props) => {
  const dataCardId = "3";
  const [activeCardPanel, setActiveCardPanel] = useState<any[]>(props?.activeKey?.includes(dataCardId) ? ["1"] : []);
  const [loading, setLoading] = useState(false);
  const [keyNumbersData, setKeyNumbersData, clearStore] = useKeyNumbersStore((state) => [state.keyNumbersData, state.setKeyNumbersData, state.clearStore]);
  const isCentric = centricClientCheck();

  const predictedCostForNext12Months =
    get(keyNumbersData, "predictedCostSaving") >= 0 && get(keyNumbersData, "predictedCostSaving") !== null
      ? `$ ${parseInt(get(keyNumbersData, "predictedCostSaving", 0))?.toLocaleString()}`
      : get(keyNumbersData, "predictedCostSaving")
      ? get(keyNumbersData, "predictedCostSaving")
      : "--";

  useEffect(() => {
    setActiveCardPanel(props?.activeKey?.includes(dataCardId) ? ["1"] : []);
    if (!keyNumbersData && props?.activeKey?.includes(dataCardId)) {
      getKeyNumbersData(["1"]);
    }
  }, [props?.activeKey]);

  const onCollapseChange = () => {
    if (activeCardPanel.length > 0) {
      props?.setActivePanels(cloneDeep(props?.activeKey)?.filter((item: any) => item !== dataCardId));
    } else {
      props?.setActivePanels([...(props.activeKey ? props.activeKey : []), dataCardId]);
    }
  };

  const patientID = useMemo(() => {
    const cMember = membersService?.getCurrentMember;
    return cMember?.patient_id;
  }, [membersService?.getCurrentMember?.patient_id]);

  const getKeyNumbersData = (panelExpanded: any) => {
    if (panelExpanded?.length > 0) {
      setLoading(true);
      memberDetailsService.getKeyNumbersRecords(patientID).subscribe({
        next: (response) => {
          const data = response?.data?.data?.keyNumbers;
          console.log("setting key numbers to ", data);
          setKeyNumbersData(data);
          setLoading(false);
        },
        error: (e) => {
          console.log(e);
          setLoading(false);
          console.log("setting key numbers to ", null);
          setKeyNumbersData(null);
        },
      });
    } else console.log("Data card is available, no API call");
  };

  useEffect(() => {
    if (keyNumbersData?.patientId?.toString() !== patientID?.toString()) {
      console.log("setting key numbers to ", null);
      clearStore();
    }
  }, [patientID]);

  const genExtra = (id: any) => <PinUnpin marginRight={"16px"} dataCardId={id} changeHandler={props?.pinChangeHandler} pinCardIds={props.pinCardIds} />;

  const getHeading = (headerValue: any) => (
    <>
      <span>{headerValue}</span>
      <CustomTooltip toolTipvalue={headerValue} children={<span style={{ margin: "8px" }} className="icon icon-info clr-lightGrayColor"></span>} />
    </>
  );

  const progressCards = [
    {
      name: "Utilization Risk",
      value: keyNumbersData?.utilizationRiskScore,
      color: getProgressColorCode(keyNumbersData?.utilizationRiskFlag),
      style: { padding: "0px", width: "11.25rem" },
    },
    {
      name: "Admission Risk",
      value: keyNumbersData?.admissionRiskScore,
      color: getProgressColorCode(keyNumbersData?.admissionRiskFlag),
      style: { padding: "0px", width: "11.25rem" },
    },
    {
      name: "Re-Admission Risk",
      value: keyNumbersData?.readmissionRiskScore,
      color: getProgressColorCode(keyNumbersData?.readmissionRiskFlag),
      style: { padding: "0px", width: "11.25rem" },
    },
    {
      name: "ER Event Risk",
      value: keyNumbersData?.ereventsRiskScore,
      color: getProgressColorCode(keyNumbersData?.eradmissionRiskFlag),
      style: { padding: "0px", width: "11.25rem" },
    },
    {
      name: "EOL Risk",
      value: keyNumbersData?.eolRiskScore,
      color: getProgressColorCode(keyNumbersData?.eolRiskScoreFlag),
      style: { padding: "0px", width: "11.25rem" },
    },
  ];

  return (
    <>
      <Collapse accordion activeKey={activeCardPanel} expandIconPosition="right" bordered={false} onChange={onCollapseChange}>
        <Panel header={getHeading("Key Numbers")} key="1" className="title" extra={genExtra(dataCardId)} style={{ marginBottom: "8px" }}>
          {loading ? (
            <Spin spinning={loading} indicator={<HealthemSpinner size={50} />}></Spin>
          ) : keyNumbersData ? (
            <>
              {!isCentric && (
                <Row gutter={16} className="legends">
                  <Col span={1}>
                    {/* <p> */}
                    <div className="legends-circle">{keyNumbersData?.patientRegularGapsCount}</div> {/* </p> */}
                  </Col>
                  <Col span={2}>
                    <span className="legends--text">Gap</span>
                  </Col>

                  <Col span={1}>
                    <div className="legends-circle">{keyNumbersData?.patientRecommendationCount}</div>
                  </Col>
                  <Col span={4}>
                    <span className="legends--text">Recommendation</span>
                  </Col>

                  <Col span={1}>
                    <div className="legends-circle">{keyNumbersData?.patientAlertsCount}</div>
                  </Col>
                  <Col span={4}>
                    <span className="legends--text">Unseen Alerts</span>
                  </Col>
                </Row>
              )}

              <Row gutter={16} className="key__numbers">
                {isCentric ? (
                  <>
                    <Col style={{ width: "11rem" }} className="key-number-heading padding__none">
                      <Card>
                          <Row>HCC Risk Score - PY { new Date().getFullYear()}</Row>
                        <Row className={"key-number-value"} style={{ color: getProgressColorCode(keyNumbersData?.PreviousYearHCCRiskScoreFlag) }}>
                          {keyNumbersData?.previousYearHCCRiskScore}
                        </Row>
                      </Card>
                    </Col>
                    <Col style={{ width: "11rem" }} className="key-number-heading padding__none">
                      <Card>
                          <Row>HCC Risk Score - PY { new Date().getFullYear() + 1}</Row>
                        <Row className={"key-number-value"} style={{ color: getProgressColorCode(keyNumbersData?.currentYearHccRiskScoreFlag) }}>
                          {keyNumbersData?.currentYearHccRiskScore}
                        </Row>
                      </Card>
                    </Col>
                  </>
                ) : null}

                <Col style={{ width: "11rem" }} className="key-number-heading padding__none">
                  <Card>
                    <Row>{!isCentric ? "HCC Score YTD" : "HCC Cumulative Score"}</Row>
                    <Row className={"key-number-value"} style={{ color: getProgressColorCode(keyNumbersData?.patientHCCRiskFlag) }}>
                      {keyNumbersData?.hccRisk}
                    </Row>
                  </Card>
                  {!isCentric && (
                    <>
                      <Card>
                        <Row>CDPS Risk</Row>
                        <Row className={keyNumbersData?.patientCPDSRiskFlag === "G" ? "key-number-green" : keyNumbersData?.patientCPDSRiskFlag === "B" ? "key-number-grey" : "key-number-red"}>
                          {keyNumbersData?.cpdsRisk}
                        </Row>
                      </Card>
                      <Card>
                        <Row>Predicted Costs</Row>
                        <Row className="f12 w400">(for next 12 Months)</Row>
                        <Row className="key-number-value">
                          {predictedCostForNext12Months}
                          {/* {get( props, 'data.keyNumbers.predictedCostSaving') ? `$ ${isNaN(predictedCostForNext12Months) ? predictedCostForNext12Months : parseInt(predictedCostForNext12Months)?.toLocaleString()}` : <span className="key-number-grey">{'NA'}</span>} */}
                        </Row>
                      </Card>
                    </>
                  )}
                </Col>

                {progressCards.map(
                  (card, index) => (!isCentric || index === 1) && <ProgressCard key={index} cardHeight={296} name={card.name} value={card.value} color={card.color} style={card.style} />
                )}
              </Row>
            </>
          ) : (
            <DataNotFound message={NO_DATA_FOUND} />
          )}
        </Panel>
      </Collapse>
    </>
  );
};

export default KeyNumbers;
